import { Field, FieldLabel } from '@HoudiniUI/Field';
import { Input } from '@HoudiniUI/Input';
import { THEME } from '@HoudiniUI/Theme/theme';
import { ChevronDown } from '@WebComponents/svgs/icons/ChevronDown';
import { GripVerticalIcon } from '@WebComponents/svgs/icons/GripVerticalIcon';
import { PlusIcon2 } from '@WebComponents/svgs/icons/PlusIcon';
import { TrashIcon } from '@WebComponents/svgs/icons/TrashIcon';
import { css, StyleSheet } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, DraggableProvided, Droppable, DropResult } from 'react-beautiful-dnd';
import { FieldValues, useFieldArray, useFormContext } from 'react-hook-form';
import { ButtonBase } from '../../../../../web/components/Button';

// components-v2 has no dashed-container / drag-row / grip-handle primitive, so the row shell,
// the drag handle, and the inline text actions are styled ButtonBase/native elements here.
// THEME has no shadow scale — the two Nav Menu Builder (1a) shadows are inlined per spec.
const SHADOW_SM = '0 1px 2px 0 rgb(0 0 0 / 0.05)';

interface NavLinkBuilderProps {
	/** Field-array path in the surrounding form: `menuLinks` (header) or `links` (footer). */
	name: string;
	/** Header links allow one level of dropdown children; footer links are flat. */
	nested?: boolean;
}

const EMPTY_LINK = { label: '', destination: '' };

type ToggleExpanded = (id: string) => void;
type ChildMover = (from: number, to: number) => void;

export function NavLinkBuilder({ name, nested = false }: NavLinkBuilderProps) {
	const { control } = useFormContext<FieldValues>();
	const { fields, append, remove, move } = useFieldArray({ control, name });

	const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
	const toggleExpanded: ToggleExpanded = id =>
		setExpandedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});

	// Child field arrays live inside each row component, so their `move` handlers are registered
	// here to keep every list under one DragDropContext (react-beautiful-dnd's supported shape).
	const childMovers = useRef<Map<string, ChildMover>>(new Map());
	const registerChildMover = (id: string, mover: ChildMover) => childMovers.current.set(id, mover);
	const unregisterChildMover = (id: string) => childMovers.current.delete(id);

	const topDroppableId = `nav-links-${name}`;

	const onDragEnd = (result: DropResult) => {
		const { source, destination } = result;
		if (!destination) {
			return;
		}
		if (source.droppableId === topDroppableId) {
			if (source.index !== destination.index) {
				move(source.index, destination.index);
			}
			return;
		}
		// Child droppables are type-scoped per parent, so a drop can only land in its own list.
		if (source.droppableId === destination.droppableId && source.index !== destination.index) {
			const parentId = source.droppableId.replace(`${topDroppableId}-children-`, '');
			childMovers.current.get(parentId)?.(source.index, destination.index);
		}
	};

	return (
		<div className={css(s.container)}>
			{fields.length === 0 ? (
				<div className={css(s.emptyState)}>No links yet. Add your first one below.</div>
			) : (
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId={topDroppableId} type={topDroppableId}>
						{provided => (
							<div ref={provided.innerRef} {...provided.droppableProps} className={css(s.list)}>
								{fields.map((field, index) => (
									<NavLinkRow
										key={field.id}
										fieldId={field.id}
										name={name}
										index={index}
										nested={nested}
										topDroppableId={topDroppableId}
										expandedIds={expandedIds}
										toggleExpanded={toggleExpanded}
										onRemove={() => remove(index)}
										appendTopLevel={value => append(value)}
										registerChildMover={registerChildMover}
										unregisterChildMover={unregisterChildMover}
									/>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			)}
			<ButtonBase className={css(s.addButton)} onClick={() => append({ ...EMPTY_LINK })}>
				<PlusIcon2 width={16} height={16} />
				Add link
			</ButtonBase>
		</div>
	);
}

interface NavLinkRowProps {
	fieldId: string;
	name: string;
	index: number;
	nested: boolean;
	topDroppableId: string;
	expandedIds: Set<string>;
	toggleExpanded: ToggleExpanded;
	onRemove: () => void;
	appendTopLevel: (value: typeof EMPTY_LINK) => void;
	registerChildMover: (id: string, mover: ChildMover) => void;
	unregisterChildMover: (id: string) => void;
}

function NavLinkRow({
	fieldId,
	name,
	index,
	nested,
	topDroppableId,
	expandedIds,
	toggleExpanded,
	onRemove,
	appendTopLevel,
	registerChildMover,
	unregisterChildMover,
}: NavLinkRowProps) {
	const { control, register, watch, getValues, setValue } = useFormContext<FieldValues>();
	const childName = `${name}.${index}.children`;
	const {
		fields: childFields,
		append: appendChild,
		remove: removeChild,
		move: moveChild,
	} = useFieldArray({ control, name: childName });

	useEffect(() => {
		if (!nested) {
			return;
		}
		registerChildMover(fieldId, moveChild);
		return () => unregisterChildMover(fieldId);
	}, [fieldId, moveChild, nested, registerChildMover, unregisterChildMover]);

	const label = watch(`${name}.${index}.label`) as string | undefined;
	const destination = watch(`${name}.${index}.destination`) as string | undefined;
	const hasChildren = nested && childFields.length > 0;
	const expanded = expandedIds.has(fieldId);

	const labelId = `${name}-${index}-label`;
	const linkId = `${name}-${index}-link`;

	// A parent that has children is a menu header and must not carry its own destination
	// (enforced by navLinkSchema: destination.max(0) when children exist). The Link input is
	// hidden for such a row, so self-heal here — covers legacy/API-imported data that loads with
	// both children and a destination, which would otherwise block Save with no UI remedy. Only
	// fires in that abnormal case, so normal (destination-less) headers are never marked dirty.
	useEffect(() => {
		if (hasChildren && destination) {
			setValue(`${name}.${index}.destination`, '', { shouldDirty: true });
		}
	}, [hasChildren, destination, name, index, setValue]);

	const handleAddChild = () => {
		appendChild({ ...EMPTY_LINK });
	};

	return (
		<Draggable draggableId={fieldId} index={index}>
			{(provided: DraggableProvided) => (
				<div ref={provided.innerRef} {...provided.draggableProps} className={css(s.rowWrapper)}>
					<div className={css(s.row)}>
						<span className={css(s.grip)} {...provided.dragHandleProps} aria-label='Drag to reorder'>
							<GripVerticalIcon width={18} height={18} />
						</span>
						<ButtonBase className={css(s.rowToggle)} onClick={() => toggleExpanded(fieldId)} aria-expanded={expanded}>
							<span className={css(s.rowText)}>
								<span className={css(s.rowLabel)}>{label?.trim() || 'Untitled link'}</span>
								{destination ? <span className={css(s.rowSubline)}>{destination}</span> : null}
							</span>
							<ChevronDown className={css(s.caret, expanded && s.caretOpen)} width={16} height={16} />
						</ButtonBase>
					</div>

					{expanded && (
						<div className={css(s.editForm)}>
							<Field>
								<FieldLabel htmlFor={labelId} styles={[s.inlineLabel]}>
									Label
								</FieldLabel>
								<Input id={labelId} {...register(`${name}.${index}.label`)} />
							</Field>
							{!hasChildren && (
								<Field>
									<FieldLabel htmlFor={linkId} styles={[s.inlineLabel]}>
										Link
									</FieldLabel>
									<Input id={linkId} placeholder='https://' {...register(`${name}.${index}.destination`)} />
								</Field>
							)}

							{nested && childFields.length > 0 && (
								<Droppable droppableId={`${topDroppableId}-children-${fieldId}`} type={`children-${fieldId}`}>
									{childProvided => (
										<div ref={childProvided.innerRef} {...childProvided.droppableProps} className={css(s.childList)}>
											{childFields.map((childField, childIndex) => (
												<ChildLinkRow
													key={childField.id}
													fieldId={childField.id}
													parentName={name}
													parentIndex={index}
													childIndex={childIndex}
													expandedIds={expandedIds}
													toggleExpanded={toggleExpanded}
													onRemove={() => removeChild(childIndex)}
													onMoveToTopLevel={() => {
														const value = getValues(`${name}.${index}.children.${childIndex}`) as
															| typeof EMPTY_LINK
															| undefined;
														appendTopLevel({
															label: value?.label ?? '',
															destination: value?.destination ?? '',
														});
														removeChild(childIndex);
													}}
												/>
											))}
											{childProvided.placeholder}
										</div>
									)}
								</Droppable>
							)}

							<div className={css(s.editFooter)}>
								{nested ? (
									<ButtonBase className={css(s.textAction)} onClick={handleAddChild}>
										<PlusIcon2 width={14} height={14} />
										Add dropdown link
									</ButtonBase>
								) : (
									<span />
								)}
								<ButtonBase className={css(s.deleteAction)} onClick={onRemove} aria-label='Delete link'>
									<TrashIcon fillColor='currentColor' />
								</ButtonBase>
							</div>
						</div>
					)}
				</div>
			)}
		</Draggable>
	);
}

interface ChildLinkRowProps {
	fieldId: string;
	parentName: string;
	parentIndex: number;
	childIndex: number;
	expandedIds: Set<string>;
	toggleExpanded: ToggleExpanded;
	onRemove: () => void;
	onMoveToTopLevel: () => void;
}

function ChildLinkRow({
	fieldId,
	parentName,
	parentIndex,
	childIndex,
	expandedIds,
	toggleExpanded,
	onRemove,
	onMoveToTopLevel,
}: ChildLinkRowProps) {
	const { register, watch } = useFormContext<FieldValues>();
	const path = `${parentName}.${parentIndex}.children.${childIndex}`;
	const label = watch(`${path}.label`) as string | undefined;
	const destination = watch(`${path}.destination`) as string | undefined;
	const expanded = expandedIds.has(fieldId);

	const labelId = `${path}-label`;
	const linkId = `${path}-link`;

	return (
		<Draggable draggableId={fieldId} index={childIndex}>
			{(provided: DraggableProvided) => (
				<div ref={provided.innerRef} {...provided.draggableProps} className={css(s.childWrapper)}>
					<div className={css(s.row, s.childRow)}>
						<span className={css(s.grip)} {...provided.dragHandleProps} aria-label='Drag to reorder'>
							<GripVerticalIcon width={16} height={16} />
						</span>
						<ButtonBase className={css(s.rowToggle)} onClick={() => toggleExpanded(fieldId)} aria-expanded={expanded}>
							<span className={css(s.rowText)}>
								<span className={css(s.rowLabel)}>{label?.trim() || 'Untitled link'}</span>
								{destination ? <span className={css(s.rowSubline)}>{destination}</span> : null}
							</span>
							<ChevronDown className={css(s.caret, expanded && s.caretOpen)} width={16} height={16} />
						</ButtonBase>
					</div>

					{expanded && (
						<div className={css(s.editForm)}>
							<Field>
								<FieldLabel htmlFor={labelId} styles={[s.inlineLabel]}>
									Label
								</FieldLabel>
								<Input id={labelId} {...register(`${path}.label`)} />
							</Field>
							<Field>
								<FieldLabel htmlFor={linkId} styles={[s.inlineLabel]}>
									Link
								</FieldLabel>
								<Input id={linkId} placeholder='https://' {...register(`${path}.destination`)} />
							</Field>
							<div className={css(s.editFooter)}>
								<ButtonBase className={css(s.textAction)} onClick={onMoveToTopLevel}>
									<OutdentIcon />
									Move to top level
								</ButtonBase>
								<ButtonBase className={css(s.deleteAction)} onClick={onRemove} aria-label='Delete link'>
									<TrashIcon fillColor='currentColor' />
								</ButtonBase>
							</div>
						</div>
					)}
				</div>
			)}
		</Draggable>
	);
}

function OutdentIcon() {
	return (
		<svg
			width={14}
			height={14}
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth={2}
			strokeLinecap='round'
			strokeLinejoin='round'
			aria-hidden
		>
			<path d='M21 6H11' />
			<path d='M21 12H11' />
			<path d='M21 18H11' />
			<path d='m7 8-4 4 4 4' />
		</svg>
	);
}

const s = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	emptyState: {
		border: `1.5px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.md,
		textAlign: 'center',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	rowWrapper: {
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.light,
		boxShadow: SHADOW_SM,
	},
	childWrapper: {
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.main,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		minHeight: 46,
		paddingLeft: THEME.spacing.sm,
		paddingRight: THEME.spacing.sm,
	},
	childRow: {
		minHeight: 42,
	},
	grip: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: THEME.colors.icon.gray,
		cursor: 'grab',
		flexShrink: 0,
		padding: THEME.spacing.xs,
		':active': {
			cursor: 'grabbing',
		},
	},
	rowToggle: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		minWidth: 0,
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.xs}px`,
		textAlign: 'left',
	},
	rowText: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	rowLabel: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
	},
	rowSubline: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		...THEME.common.truncate,
	},
	caret: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		transition: 'transform 0.15s ease',
	},
	caretOpen: {
		transform: 'rotate(180deg)',
	},
	editForm: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	inlineLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: THEME.colors.text.light,
	},
	childList: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		marginLeft: 26,
	},
	editFooter: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	textAction: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.primary.main,
	},
	deleteAction: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: THEME.spacing.xs,
		color: THEME.colors.text.light,
		':hover': {
			color: THEME.colors.icon.error,
		},
	},
	addButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.xs,
		width: '100%',
		border: `1.5px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: 'none',
		cursor: 'pointer',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
		':hover': {
			borderColor: THEME.colors.primary.main,
			color: THEME.colors.primary.main,
		},
	},
});
